import React, { useMemo } from 'react';
import { Trans } from '@lingui/macro';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { ListWrapper } from '../../../../components/lists/ListWrapper';
import { useAppDataContext } from '../../../../hooks/app-data-provider/useAppDataProvider';
import { useLeveragedPositionsDataQuery } from '../../../../hooks/yldr/useLeveragedPositionsDataQuery';
import { useRootStore } from '../../../../store/root';
import { selectEnrichedLeveragedUniswapPosition } from '../../../../store/positionSelectors';
import { PositionsList } from '../../../../components/PositionsList/PositionsList';
import { ValueInformer } from '../../../../components-yldr/ValueInformer';
import { TextWithTooltip } from '../../../../components/TextWithTooltip';
import { useLeveragedErc20PositionsDataQuery } from '../../../../hooks/yldr/useLeveragedErc20PositionsDataQuery';
import { LeveragedActions } from '../../../../components/PositionsList/components/UniswapPositionsActions/LeveragedActions';
import { LeveragedSkeleton } from './LeveragedSkeleton';

const head = [
  {
    title: <Trans>Pool</Trans>,
    sortKey: 'pool',
  },
  {
    title: <Trans key="Liquidity">Liquidity</Trans>,
    sortKey: 'liquidity',
  },
  {
    title: <Trans key="debtValue">Debt Value</Trans>,
    sortKey: 'debtValue',
  },
  {
    title: <Trans key="feesEarned">Fees Earned</Trans>,
    sortKey: 'feesEarned',
  },
  {
    title: (
      <>
        <Trans key="netWorth">Net Worth</Trans>
        <TextWithTooltip wrapperProps={{ ml: 1 }}>
          <div>Net worth = Liquidity - Debt Value + Fees Earned</div>
        </TextWithTooltip>
      </>
    ),
    sortKey: 'netWorth',
  },
  {
    title: <Trans key="borrowAPY">Est. Net APR</Trans>,
    sortKey: 'estNetApr',
  },
  {
    title: <Trans key="health">Health</Trans>,
    sortKey: 'health',
  },
];

export const LeveragedPositionsList = () => {
  const { breakpoints } = useTheme();
  const { loading: isLoadingAppDataContext } = useAppDataContext();
  const md = useMediaQuery(breakpoints.up('md'));

  const { data: leveragedPositions, isLoading: isLoadingPositions } = useLeveragedPositionsDataQuery();
  const { data: leveragedErc20Positions, isLoading: isLoadingErc20Positions } = useLeveragedErc20PositionsDataQuery();

  const enrichedLeveragedPositionsData = useRootStore((state) =>
    selectEnrichedLeveragedUniswapPosition(state, [...leveragedPositions, ...leveragedErc20Positions]));

  const { totalLiquidity, totalFeesEarned, totalNetWorth } = useMemo(
    () => enrichedLeveragedPositionsData.reduce((acc, item) => {
      acc.totalLiquidity += item.marketReferenceCurrencyValue;
      acc.totalFeesEarned += item.token0.marketReferenceCurrencyFee + item.token1.marketReferenceCurrencyFee;
      acc.totalNetWorth += item.token0.netWorthUsd + item.token1.netWorthUsd;
      return acc;
    }, { totalLiquidity: 0, totalFeesEarned: 0, totalNetWorth: 0 }),
    [leveragedPositions, leveragedErc20Positions]
  );

  const isLoading = isLoadingAppDataContext || isLoadingPositions || isLoadingErc20Positions;

  if (isLoading) {
    return (
      <LeveragedSkeleton
        title={<Trans>Your Leveraged Positions</Trans>}
        head={head.map((col) => col.title)}
      />
    )
  }

  return (
    <ListWrapper
      titleComponent={
        <Typography
          component="div"
          variant="h3"
          sx={{
            mr: 4,
            mb: { xs: 0, md: 2 }
          }}
        >
          <Trans>Your Leveraged Positions</Trans>
        </Typography>
      }
      subTitleComponent={
        <Box sx={{ display: 'flex', gap: 6 }}>
          <ValueInformer
            caption="Total Liquidity"
            value={totalLiquidity}
            symbol="USD"
          />
          <ValueInformer
            caption="Fees Earned"
            value={totalFeesEarned}
            symbol="USD"
          />
          <ValueInformer
            caption="Net Worth"
            value={totalNetWorth}
            symbol="USD"
          />
        </Box>
      }
      localStorageName="leveragedPositionsTableCollapse"
      noData={!enrichedLeveragedPositionsData.length}
      hasCollapseButton={!md}
    >
      <PositionsList
        head={head}
        positions={enrichedLeveragedPositionsData}
        actions={(args) => (
          <LeveragedActions
            marketId={args.position.marketId}
            positionAddress={args.positionAddress}
            controlType={args.position.controlType}
          />
        )}
        columnsType='leveraged'
      />
    </ListWrapper>
  );
};
